/**
 * Module dependencies.
 */

import { Control } from 'react-hook-form';
import { InputField } from 'src/components/core/forms/input/field';
import { SelectField } from 'src/components/core/forms/select/field';
import { emailRules, maxDateRule, requiredRule } from 'src/core/forms/rules';
import { serviceGenderEnum } from 'src/core/constants/enums';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import calendarSvg from 'src/assets/svg/calendar.svg';
import camelCase from 'lodash/camelCase';

/**
 * `Props` type.
 */

type Props = {
  control: Control<any>;
  hideBirthDate?: boolean;
  hideGender?: boolean;
};

/**
 * Export `UserInfoFields` component.
 */

export function UserInfoFields(props: Props) {
  const { t } = useTranslation('common');
  const { control, hideBirthDate, hideGender } = props;
  const todayDate = useMemo(() => new Date().toISOString(), []);
  const genderOptions = useMemo(
    () =>
      Object.keys(serviceGenderEnum).map(value => ({
        label: t(`common:service.subCategory.${camelCase(value)}`),
        value
      })),
    [t]
  );

  return (
    <>
      <InputField
        control={control}
        isRequired
        label={t('form.labels.name')}
        name={'name'}
        placeholder={t('form.placeholders.name')}
        rules={requiredRule(t)}
      />

      <InputField
        control={control}
        isRequired
        label={t('form.labels.email')}
        name={'email'}
        placeholder={t('form.placeholders.email')}
        rules={{
          ...requiredRule(t),
          ...emailRules(t)
        }}
      />

      <InputField
        control={control}
        isRequired
        label={t('form.labels.phone')}
        name={'phone'}
        placeholder={t('form.placeholders.phone')}
        rules={requiredRule(t)}
      />

      {!hideBirthDate && (
        <InputField
          control={control}
          icon={calendarSvg}
          iconType={'button'}
          isRequired
          label={t('form.labels.birthDate')}
          name={'dateOfBirth'}
          rules={{
            ...requiredRule(t),
            ...maxDateRule(todayDate, t)
          }}
          type={'date'}
        />
      )}

      {!hideGender && (
        <SelectField
          control={control}
          isRequired
          label={t('common:form.labels.gender')}
          name={'gender'}
          options={genderOptions}
          rules={requiredRule(t)}
        />
      )}
    </>
  );
}
